import React, { useEffect, useMemo, useState, useRef, useContext, createContext } from "react";
import api from "@/lib/api";
import { downloadFile } from "@/lib/api";
import { brl, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FileText, TrendingUp, Landmark, Layers, Building2, ArrowDownCircle, ArrowUpCircle,
  Users, Receipt, HardHat, CalendarRange, ScrollText, Loader2, ChevronRight, Printer, FileSpreadsheet,
} from "lucide-react";

const PRINT_CSS = `
*{box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;color:#1e293b;margin:0;padding:0 22px 64px}
input,button,[role=combobox],label,svg{display:none!important}
.pdf-header{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #123826;padding:14px 0;margin-bottom:6px}
.pdf-brand{font-size:20px;font-weight:800;color:#123826;letter-spacing:.5px}
.pdf-brand span{display:block;font-size:10px;font-weight:700;color:#C5A059;letter-spacing:2px;text-transform:uppercase}
.pdf-meta{text-align:right;font-size:11px;color:#64748b;line-height:1.5}
.pdf-title{font-size:17px;color:#123826;margin:12px 0 4px}
.pdf-footer{position:fixed;bottom:0;left:0;right:0;text-align:center;font-size:9px;color:#94a3b8;border-top:1px solid #e2e8f0;padding:6px}
table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:10px}
th,td{padding:6px 8px;border-bottom:1px solid #e2e8f0;text-align:left;vertical-align:top}
th{font-size:9px;text-transform:uppercase;letter-spacing:.4px;color:#64748b;border-bottom:2px solid #cbd5e1}
.text-right{text-align:right!important}
.font-mono{font-family:'Courier New',monospace}
.font-semibold,.font-bold{font-weight:700}
.text-rose-600,.text-rose-700{color:#e11d48}
.text-emerald-600,.text-emerald-700{color:#059669}
.text-brand{color:#123826}
[class*="bg-slate-50"]{background:#f5f7fa}
.bg-brand-light{background:#eaf1ec}
.bg-rose-50{background:#fff1f2}
[class*="bg-amber-50"]{background:#fffbeb}
.grid{display:block}
.rounded-xl,.rounded-lg,.border{border:1px solid #e2e8f0;border-radius:6px;margin-bottom:6px}
h2,h3,h4{color:#123826;font-size:13px;margin:8px 0}
@page{margin:12mm 9mm}
`;

function printReport(html, title, userName, meta) {
  const w = window.open("", "_blank", "width=1024,height=800");
  if (!w) return;
  const now = new Date().toLocaleString("pt-BR");
  w.document.write(`<!doctype html><html lang="pt-BR" translate="no"><head><meta charset="utf-8"><title>${title} — Clube Monte Líbano</title><style>${PRINT_CSS}</style></head>
  <body>
    <div class="pdf-header">
      <div class="pdf-brand">CLUBE MONTE LÍBANO<span>Prestação / Relatório Financeiro</span></div>
      <div class="pdf-meta">Gerado em ${now}<br>Responsável: ${userName || "—"}</div>
    </div>
    <div class="pdf-title">${title}</div>
    ${meta ? `<div style="font-size:11px;color:#64748b;margin-bottom:8px">${meta}</div>` : ""}
    <div class="pdf-body">${html}</div>
    <div class="pdf-footer">Clube Monte Líbano — ${title} — Documento gerado pelo sistema de Gestão Financeira</div>
    <script>window.onload=function(){setTimeout(function(){window.print();},350);};</script>
  </body></html>`);
  w.document.close();
}

const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const ymd = (d) => d.toISOString().slice(0, 10);
const yearStart = () => `${new Date().getFullYear()}-01-01`;
const todayStr = () => ymd(new Date());

const REPORTS = [
  { id: "income-statement", label: "Demonstrativo de Receitas e Despesas", icon: FileText },
  { id: "cashflow", label: "Fluxo de Caixa", icon: TrendingUp },
  { id: "bank-balances", label: "Saldos Bancários e Disponibilidades", icon: Landmark },
  { id: "expenses-by-cost-center", label: "Despesas por Centro de Custo", icon: Building2 },
  { id: "expenses-by-category", label: "Despesas por Categoria", icon: Layers },
  { id: "cost-center-category", label: "Centro de Custo × Categoria", icon: Building2 },
  { id: "payables-aging", label: "Contas a Pagar", icon: ArrowDownCircle },
  { id: "receivables-aging", label: "Contas a Receber", icon: ArrowUpCircle },
  { id: "payments-by-supplier", label: "Pagamentos por Fornecedor", icon: Users },
  { id: "movements", label: "Movimentação Financeira", icon: Receipt },
  { id: "works", label: "Obras e Reformas", icon: HardHat },
  { id: "monthly-comparison", label: "Comparativo Mensal e Anual", icon: CalendarRange },
  { id: "audit", label: "Auditoria de Lançamentos", icon: ScrollText },
];

function Th({ children, right }) { return <th className={`px-4 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider ${right ? "text-right" : "text-left"}`}>{children}</th>; }
function Td({ children, right, bold, mono, cls = "" }) { return <td className={`px-4 py-3 ${right ? "text-right" : ""} ${bold ? "font-semibold" : ""} ${mono ? "font-mono" : ""} ${cls}`}>{children}</td>; }
function Table({ children, testId }) {
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm" data-testid={testId}>{children}</table></div></div>;
}
function DateRange({ start, end, setStart, setEnd }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Data inicial</label>
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="rep-start" className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm" /></div>
      <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Data final</label>
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="rep-end" className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm" /></div>
    </div>
  );
}
function Regime() { return <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-md text-xs">Regime: <strong>Caixa</strong></span>; }
function Loading() { return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>; }

const ExportCtx = createContext(null);

function useReport(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const registerExport = useContext(ExportCtx);
  const reload = () => { setLoading(true); api.get(url).then((r) => setData(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [url]);
  useEffect(() => {
    if (!registerExport) return;
    const [path, qs] = url.split("?");
    const ex = path.replace("/reports/", "/reports/export/") + ".xlsx" + (qs ? `?${qs}` : "");
    registerExport(ex);
    /* eslint-disable-next-line */
  }, [url]);
  return { data, loading, reload };
}

function DrillModal({ title, url, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { setLoading(true); api.get(url).then((r) => setData(r.data)).finally(() => setLoading(false)); }, [url]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="rep-drill-modal">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>Lançamentos que compõem este valor (regime de caixa).</DialogDescription></DialogHeader>
        {loading ? <Loading /> : !data || data.rows.length === 0 ? <EmptyState text="Sem lançamentos." /> : (
          <div className="space-y-2">
            <div className="text-sm text-slate-500">{data.count} lançamento(s) • Total <strong className="text-slate-800 font-mono">{brl(data.total)}</strong></div>
            <div className="max-h-[55vh] overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {data.rows.map((it, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`rep-drill-row-${i}`}>
                  <div className="min-w-0"><div className="text-slate-800 truncate">{it.description}</div>
                    <div className="text-xs text-slate-400">{fmtDate(it.date)} • {it.supplier !== "—" ? it.supplier + " • " : ""}{it.category} • {it.cost_center}</div></div>
                  <div className="font-mono font-semibold text-slate-900 whitespace-nowrap">{brl(it.amount)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- 1. Demonstrativo ---------- */
function IncomeStatement() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const { data, loading } = useReport(`/reports/income-statement?year=${year}${month ? `&month=${month}` : ""}`);
  const [exp, setExp] = useState({});
  const [drill, setDrill] = useState(null);
  const s = month ? `${year}-${String(month).padStart(2, "0")}-01` : `${year}-01-01`;
  const e = month ? `${year}-${String(month).padStart(2, "0")}-${new Date(year, month, 0).getDate()}` : `${year}-12-31`;
  const Section = ({ title, rows, total, kind }) => (
    <>
      <tr className="bg-slate-50"><td colSpan={3} className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-600">{title}</td></tr>
      {rows.map((r) => (
        <tr key={(r.category_id || "sem") + kind} className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" data-testid={`is-${kind}-${r.category_id || "sem"}`}
            onClick={() => r.category_id && setDrill({ title: r.name, url: `/reports/transactions?start=${s}&end=${e}&kind=${kind}&category_id=${r.category_id}` })}>
          <Td><span className="inline-flex items-center gap-1 text-slate-700">{r.category_id && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}{r.name}</span></Td>
          <Td right mono cls="text-slate-600">{brl(r.mes)}</Td>
          <Td right mono cls="text-slate-500">{brl(r.acumulado)}</Td>
        </tr>
      ))}
      <tr className="border-b-2 border-slate-200 font-semibold"><Td cls="text-slate-800">TOTAL {title}</Td><Td right mono>{brl(total.mes)}</Td><Td right mono cls="text-slate-500">{brl(total.acumulado)}</Td></tr>
    </>
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Exercício</label>
          <input type="number" value={year} onChange={(ev) => setYear(parseInt(ev.target.value) || now.getFullYear())} data-testid="is-year" className="w-24 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Mês</label>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="w-[160px] text-sm" data-testid="is-month"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="0">Ano inteiro</SelectItem>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select></div>
        <Regime />
      </div>
      {loading ? <Loading /> : !data ? <EmptyState /> : (
        <Table testId="is-table">
          <thead><tr className="border-b border-slate-200"><Th>Categoria</Th><Th right>{month ? "Mês" : "Período"}</Th><Th right>Acumulado</Th></tr></thead>
          <tbody>
            <Section title="RECEITAS" rows={data.receitas} total={data.total_receitas} kind="receita" />
            <Section title="DESPESAS" rows={data.despesas} total={data.total_despesas} kind="despesa" />
            <tr className="bg-brand-light font-bold"><Td cls="text-brand">RESULTADO (Receitas − Despesas)</Td>
              <Td right mono cls={data.resultado.mes < 0 ? "text-rose-600" : "text-brand"}>{brl(data.resultado.mes)}</Td>
              <Td right mono cls={data.resultado.acumulado < 0 ? "text-rose-600" : "text-brand"}>{brl(data.resultado.acumulado)}</Td></tr>
          </tbody>
        </Table>
      )}
      {drill && <DrillModal {...drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

/* ---------- 2. Fluxo de Caixa ---------- */
function CashFlow() {
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(todayStr());
  const [gran, setGran] = useState("monthly");
  const { data, loading } = useReport(`/reports/cashflow?start=${start}&end=${end}&granularity=${gran}`);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <DateRange {...{ start, end, setStart, setEnd }} />
        <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Visualização</label>
          <Select value={gran} onValueChange={setGran}><SelectTrigger className="w-[140px] text-sm" data-testid="cf-gran"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="daily">Diária</SelectItem><SelectItem value="weekly">Semanal</SelectItem><SelectItem value="monthly">Mensal</SelectItem></SelectContent></Select></div>
        <Regime />
      </div>
      {loading ? <Loading /> : !data ? <EmptyState /> : (<>
        <Table testId="cf-table">
          <thead><tr className="border-b border-slate-200"><Th>Período</Th><Th right>Saldo inicial</Th><Th right>Entradas</Th><Th right>Saídas</Th><Th right>Saldo final</Th></tr></thead>
          <tbody>
            {data.rows.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">Sem movimentações no período.</td></tr>
              : data.rows.map((b) => (
              <tr key={b.key} className="border-b border-slate-100"><Td cls="text-slate-700">{b.label}</Td>
                <Td right mono cls="text-slate-500">{brl(b.saldo_inicial)}</Td>
                <Td right mono cls="text-emerald-600">{brl(b.entradas)}</Td>
                <Td right mono cls="text-rose-600">{brl(b.saidas)}</Td>
                <Td right mono bold>{brl(b.saldo_final)}</Td></tr>
            ))}
            <tr className="bg-slate-50 font-semibold"><Td cls="text-slate-700">Saldo inicial do período: {brl(data.saldo_inicial)}</Td>
              <Td></Td><Td right mono cls="text-emerald-700">{brl(data.totais.entradas)}</Td><Td right mono cls="text-rose-700">{brl(data.totais.saidas)}</Td><Td right mono cls="text-brand">{brl(data.totais.saldo_final)}</Td></tr>
          </tbody>
        </Table>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <h4 className="font-heading font-semibold text-slate-900 text-sm mb-3">Projeção de caixa <span className="text-xs font-normal text-amber-600">(projetado — contas em aberto)</span></h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cf-projection">
            {data.projecao.map((p) => (
              <div key={p.days} className="rounded-lg border border-dashed border-amber-200 bg-amber-50/40 p-3">
                <div className="text-xs font-semibold text-amber-700">{p.days} dias</div>
                <div className="text-[11px] text-slate-500 mt-1">A receber <span className="font-mono text-emerald-600">{brl(p.a_receber)}</span></div>
                <div className="text-[11px] text-slate-500">A pagar <span className="font-mono text-rose-600">{brl(p.a_pagar)}</span></div>
                <div className="text-sm font-mono font-bold text-slate-800 mt-1">{brl(p.saldo_projetado)}</div>
              </div>
            ))}
          </div>
        </div>
      </>)}
    </div>
  );
}

/* ---------- 3. Saldos Bancários ---------- */
function BankBalances() {
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(todayStr());
  const { data, loading } = useReport(`/reports/bank-balances?start=${start}&end=${end}`);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3"><DateRange {...{ start, end, setStart, setEnd }} /></div>
      {loading ? <Loading /> : !data ? <EmptyState /> : (
        <Table testId="bb-table">
          <thead><tr className="border-b border-slate-200"><Th>Conta</Th><Th>Banco</Th><Th right>Saldo inicial</Th><Th right>Entradas</Th><Th right>Saídas</Th><Th right>Saldo atual</Th><Th>Últ. mov.</Th></tr></thead>
          <tbody>
            {data.rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 text-sm">Nenhuma conta ativa.</td></tr>
              : data.rows.map((r) => (
              <tr key={r.account_id} className="border-b border-slate-100"><Td bold cls="text-slate-800">{r.name}</Td><Td cls="text-slate-500">{r.bank || "—"}</Td>
                <Td right mono cls="text-slate-500">{brl(r.saldo_inicial)}</Td><Td right mono cls="text-emerald-600">{brl(r.entradas)}</Td>
                <Td right mono cls="text-rose-600">{brl(r.saidas)}</Td><Td right mono bold>{brl(r.saldo_atual)}</Td>
                <Td cls="text-slate-400 text-xs">{r.ultima_mov ? fmtDate(r.ultima_mov) : "—"}</Td></tr>
            ))}
            <tr className="bg-brand-light font-bold"><Td cls="text-brand" >TOTAL DE DISPONIBILIDADES</Td><Td></Td><Td></Td><Td></Td><Td></Td><Td right mono cls="text-brand">{brl(data.total_disponibilidades)}</Td><Td></Td></tr>
          </tbody>
        </Table>
      )}
    </div>
  );
}

/* ---------- 4/5. Despesas por CC / Categoria ---------- */
function ExpensesBy({ dim }) {
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(todayStr());
  const isCC = dim === "cost_center";
  const { data, loading } = useReport(`/reports/expenses-by-${isCC ? "cost-center" : "category"}?start=${start}&end=${end}`);
  const [drill, setDrill] = useState(null);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3"><DateRange {...{ start, end, setStart, setEnd }} /><Regime /></div>
      {loading ? <Loading /> : !data ? <EmptyState /> : data.rows.length === 0 ? <EmptyState text="Sem despesas no período." /> : (
        <Table testId={isCC ? "ecc-table" : "ecat-table"}>
          <thead><tr className="border-b border-slate-200"><Th>{isCC ? "Centro de Custo" : "Categoria"}</Th><Th right>Valor</Th><Th right>%</Th>
            {isCC ? <><Th right>Orçado</Th><Th right>Diferença</Th><Th right>% Exec.</Th></> : <Th right>Acum. ano</Th>}</tr></thead>
          <tbody>
            {data.rows.map((r) => {
              const id = r.cost_center_id || r.category_id;
              const url = `/reports/transactions?start=${start}&end=${end}&kind=despesa&${isCC ? "cost_center_id" : "category_id"}=${id}`;
              return (
                <tr key={id || "sem"} className={`border-b border-slate-100 ${id ? "hover:bg-slate-50/60 cursor-pointer" : ""}`} data-testid={`exp-row-${id || "sem"}`}
                    onClick={() => id && setDrill({ title: r.name, url })}>
                  <Td><span className="inline-flex items-center gap-1 text-slate-800 font-medium">{id && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}{r.name}</span></Td>
                  <Td right mono bold>{brl(r.valor)}</Td><Td right mono cls="text-slate-500">{r.percent}%</Td>
                  {isCC ? <><Td right mono cls="text-slate-500">{brl(r.orcado)}</Td>
                    <Td right mono cls={r.diferenca < 0 ? "text-rose-600" : "text-emerald-600"}>{brl(r.diferenca)}</Td>
                    <Td right mono cls="text-slate-500">{r.percent_exec == null ? "—" : r.percent_exec + "%"}</Td></>
                    : <Td right mono cls="text-slate-500">{brl(r.acumulado_ano)}</Td>}
                </tr>
              );
            })}
            <tr className="bg-slate-50 font-bold"><Td cls="text-slate-800">TOTAL</Td><Td right mono>{brl(data.total)}</Td><Td right>100%</Td>
              {isCC ? <><Td></Td><Td></Td><Td></Td></> : <Td></Td>}</tr>
          </tbody>
        </Table>
      )}
      {drill && <DrillModal {...drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

/* ---------- 6/7. Aging Contas a Pagar / Receber ---------- */
function Aging({ kind }) {
  const isPay = kind === "payables";
  const { data, loading } = useReport(`/reports/${kind}-aging`);
  return (
    <div className="space-y-4">
      {loading ? <Loading /> : !data ? <EmptyState /> : (
        <div className="space-y-3">
          <div className="text-sm text-slate-600">Total em aberto: <strong className="font-mono text-slate-900">{brl(data.total)}</strong></div>
          {data.groups.map((g) => (
            <div key={g.key} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`aging-${g.key}`}>
              <div className={`flex items-center justify-between px-4 py-2.5 ${g.key === "vencidas" ? "bg-rose-50" : "bg-slate-50"}`}>
                <span className={`font-semibold text-sm ${g.key === "vencidas" ? "text-rose-700" : "text-slate-700"}`}>{g.label}</span>
                <span className="font-mono font-semibold text-sm text-slate-800">{brl(g.total)} <span className="text-xs text-slate-400">({g.items.length})</span></span>
              </div>
              {g.items.length > 0 && (
                <table className="w-full text-sm">
                  <tbody>
                    {g.items.map((it) => (
                      <tr key={it.id} className="border-t border-slate-100">
                        <Td cls="text-slate-400 text-xs w-24">{fmtDate(it.due_date)}</Td>
                        <Td cls="text-slate-700">{isPay ? it.supplier : it.payer}{it.document ? ` • ${it.document}` : ""}</Td>
                        <Td cls="text-slate-500 text-xs">{it.description}</Td>
                        <Td right mono bold>{brl(it.amount)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- 8. Pagamentos por Fornecedor ---------- */
function PaymentsBySupplier() {
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(todayStr());
  const { data, loading } = useReport(`/reports/payments-by-supplier?start=${start}&end=${end}`);
  const [drill, setDrill] = useState(null);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3"><DateRange {...{ start, end, setStart, setEnd }} /></div>
      {loading ? <Loading /> : !data ? <EmptyState /> : data.rows.length === 0 ? <EmptyState text="Sem pagamentos no período." /> : (<>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Top 10 Fornecedores</div>
        <Table testId="pbs-table">
          <thead><tr className="border-b border-slate-200"><Th>Fornecedor</Th><Th right>Pagamentos</Th><Th right>Valor no período</Th><Th right>Acum. ano</Th><Th right>%</Th></tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.supplier_id || "sem"} className={`border-b border-slate-100 ${r.supplier_id ? "hover:bg-slate-50/60 cursor-pointer" : ""}`} data-testid={`pbs-row-${r.supplier_id || "sem"}`}
                  onClick={() => r.supplier_id && setDrill({ title: r.name, url: `/reports/transactions?start=${start}&end=${end}&kind=despesa&supplier_id=${r.supplier_id}` })}>
                <Td><span className="inline-flex items-center gap-1 text-slate-800 font-medium">{r.supplier_id && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}{r.name}</span></Td>
                <Td right cls="text-slate-500">{r.qtd}</Td><Td right mono bold>{brl(r.valor)}</Td>
                <Td right mono cls="text-slate-500">{brl(r.acumulado_ano)}</Td><Td right mono cls="text-slate-500">{r.percent}%</Td>
              </tr>
            ))}
            <tr className="bg-slate-50 font-bold"><Td cls="text-slate-800">TOTAL</Td><Td></Td><Td right mono>{brl(data.total)}</Td><Td></Td><Td right>100%</Td></tr>
          </tbody>
        </Table>
      </>)}
      {drill && <DrillModal {...drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

/* ---------- 9. Movimentação Financeira ---------- */
function Movements() {
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(todayStr());
  const [account, setAccount] = useState("");
  const [accounts, setAccounts] = useState([]);
  useEffect(() => { api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {}); }, []);
  const { data, loading } = useReport(`/reports/movements?start=${start}&end=${end}${account ? `&account_id=${account}` : ""}`);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <DateRange {...{ start, end, setStart, setEnd }} />
        <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Conta financeira</label>
          <Select value={account || "all"} onValueChange={(v) => setAccount(v === "all" ? "" : v)}><SelectTrigger className="w-[190px] text-sm" data-testid="mov-account"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas (consolidado)</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select></div>
      </div>
      {loading ? <Loading /> : !data ? <EmptyState /> : (
        <Table testId="mov-table">
          <thead><tr className="border-b border-slate-200"><Th>Data</Th><Th>Conta</Th><Th>Histórico</Th><Th right>Entrada</Th><Th right>Saída</Th><Th right>Saldo</Th></tr></thead>
          <tbody>
            <tr className="bg-slate-50 text-xs"><Td colSpan={5} cls="text-slate-500">Saldo inicial do período</Td><Td right mono bold>{brl(data.saldo_inicial)}</Td></tr>
            {data.rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400 text-sm">Sem movimentações.</td></tr>
              : data.rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100" data-testid={`mov-row-${i}`}>
                <Td cls="text-slate-400 text-xs w-24">{fmtDate(r.date)}</Td><Td cls="text-slate-500 text-xs">{r.account}</Td>
                <Td cls="text-slate-700">{r.description}</Td>
                <Td right mono cls="text-emerald-600">{r.entrada ? brl(r.entrada) : "—"}</Td>
                <Td right mono cls="text-rose-600">{r.saida ? brl(r.saida) : "—"}</Td>
                <Td right mono bold>{brl(r.saldo)}</Td>
              </tr>
            ))}
            <tr className="bg-brand-light font-bold"><Td colSpan={5} cls="text-brand">Saldo final</Td><Td right mono cls="text-brand">{brl(data.saldo_final)}</Td></tr>
          </tbody>
        </Table>
      )}
    </div>
  );
}

/* ---------- 10. Obras e Reformas ---------- */
function Works() {
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(todayStr());
  const { data, loading } = useReport(`/reports/works?start=${start}&end=${end}`);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3"><DateRange {...{ start, end, setStart, setEnd }} /></div>
      {loading ? <Loading /> : !data ? <EmptyState /> : data.rows.length === 0 ? <EmptyState text="Sem lançamentos de obras/reformas (centro 600) no período." /> : (
        <Table testId="works-table">
          <thead><tr className="border-b border-slate-200"><Th>Projeto / Centro</Th><Th right>Orçado</Th><Th right>Exec. período</Th><Th right>Exec. acumulado</Th><Th right>Saldo</Th><Th right>% Exec.</Th></tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.cost_center_id} className="border-b border-slate-100"><Td bold cls="text-slate-800">{r.name}</Td>
                <Td right mono cls="text-slate-500">{brl(r.orcado)}</Td><Td right mono>{brl(r.executado_periodo)}</Td>
                <Td right mono bold>{brl(r.executado_acumulado)}</Td>
                <Td right mono cls={r.saldo < 0 ? "text-rose-600" : "text-emerald-600"}>{brl(r.saldo)}</Td>
                <Td right mono cls="text-slate-500">{r.percent_exec == null ? "—" : r.percent_exec + "%"}</Td></tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

/* ---------- 11. Comparativo Mensal e Anual ---------- */
function MonthlyComparison() {
  const { data, loading } = useReport(`/reports/monthly-comparison`);
  const Cmp = ({ label, c }) => c == null ? null : (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="font-mono font-bold text-slate-800">{brl(c.atual)} <span className={`text-xs ${c.delta < 0 ? "text-rose-600" : "text-emerald-600"}`}>({c.delta >= 0 ? "+" : ""}{brl(c.delta)})</span></div>
    </div>
  );
  return (
    <div className="space-y-4">
      {loading ? <Loading /> : !data ? <EmptyState /> : (<>
        {data.aviso_historico && <div className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2">{data.aviso_historico}</div>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Cmp label="Resultado — mês atual x mês anterior" c={data.comparativos.mes_anterior} />
          <Cmp label="Resultado — x mesmo mês do ano anterior" c={data.comparativos.mesmo_mes_ano_anterior} />
          {data.comparativos.acumulado && <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[11px] text-slate-500">Acumulado do exercício x anterior</div>
            <div className="font-mono font-bold text-slate-800">{brl(data.comparativos.acumulado.atual)} <span className={`text-xs ${data.comparativos.acumulado.delta < 0 ? "text-rose-600" : "text-emerald-600"}`}>({data.comparativos.acumulado.delta >= 0 ? "+" : ""}{brl(data.comparativos.acumulado.delta)})</span></div></div>}
        </div>
        <Table testId="mc-table">
          <thead><tr className="border-b border-slate-200"><Th>Mês</Th><Th right>Receitas</Th><Th right>Despesas</Th><Th right>Resultado</Th><Th right>Saldo disponível</Th></tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.key} className="border-b border-slate-100"><Td cls="text-slate-700">{r.label}</Td>
                <Td right mono cls="text-emerald-600">{brl(r.receitas)}</Td><Td right mono cls="text-rose-600">{brl(r.despesas)}</Td>
                <Td right mono bold cls={r.resultado < 0 ? "text-rose-600" : ""}>{brl(r.resultado)}</Td>
                <Td right mono cls="text-slate-500">{brl(r.saldo)}</Td></tr>
            ))}
          </tbody>
        </Table>
      </>)}
    </div>
  );
}

/* ---------- 12. Auditoria ---------- */
function AuditReport() {
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(todayStr());
  const [userQ, setUserQ] = useState("");
  const [module, setModule] = useState("");
  const [operation, setOperation] = useState("");
  const url = `/reports/audit?start=${start}&end=${end}${userQ ? `&user_q=${encodeURIComponent(userQ)}` : ""}${module ? `&module=${module}` : ""}${operation ? `&operation=${operation}` : ""}`;
  const { data, loading } = useReport(url);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <DateRange {...{ start, end, setStart, setEnd }} />
        <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Usuário</label>
          <input value={userQ} onChange={(e) => setUserQ(e.target.value)} data-testid="aud-user" placeholder="Nome" className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm w-36" /></div>
        <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Módulo</label>
          <Select value={module || "all"} onValueChange={(v) => setModule(v === "all" ? "" : v)}><SelectTrigger className="w-[160px] text-sm" data-testid="aud-module"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos</SelectItem>{(data?.modules || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Operação</label>
          <Select value={operation || "all"} onValueChange={(v) => setOperation(v === "all" ? "" : v)}><SelectTrigger className="w-[150px] text-sm" data-testid="aud-operation"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas</SelectItem>{(data?.operations || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
      </div>
      {loading ? <Loading /> : !data ? <EmptyState /> : data.rows.length === 0 ? <EmptyState text="Nenhum registro de auditoria." /> : (
        <Table testId="aud-table">
          <thead><tr className="border-b border-slate-200"><Th>Data/hora</Th><Th>Usuário</Th><Th>Operação</Th><Th>Módulo</Th><Th>Descrição</Th></tr></thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100"><Td cls="text-slate-400 text-xs">{fmtDate(r.created_at)}</Td>
                <Td cls="text-slate-700">{r.user_name}</Td><Td cls="text-slate-600"><span className="bg-slate-100 px-2 py-0.5 rounded text-xs">{r.operation}</span></Td>
                <Td cls="text-slate-500 text-xs">{r.module}</Td><Td cls="text-slate-600 text-xs">{r.description}</Td></tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

/* ---------- Centro de Custo × Categoria ---------- */
function CostCenterCategory() {
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(todayStr());
  const [kind, setKind] = useState("despesa");
  const { data, loading } = useReport(`/reports/cost-center-category?start=${start}&end=${end}&kind=${kind}`);
  const [drill, setDrill] = useState(null);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <DateRange {...{ start, end, setStart, setEnd }} />
        <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="ccc-kind-toggle">
          <button onClick={() => setKind("despesa")} data-testid="ccc-kind-despesa" className={`px-3 py-2 text-sm font-medium ${kind === "despesa" ? "bg-brand text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Despesas</button>
          <button onClick={() => setKind("receita")} data-testid="ccc-kind-receita" className={`px-3 py-2 text-sm font-medium ${kind === "receita" ? "bg-brand text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Receitas</button>
        </div>
        <Regime />
      </div>
      {loading ? <Loading /> : !data ? <EmptyState /> : data.groups.length === 0 ? <EmptyState text={`Sem ${kind === "receita" ? "receitas" : "despesas"} no período.`} /> : (
        <div className="space-y-3">
          <div className="text-sm text-slate-600">Total geral: <strong className="font-mono text-slate-900">{brl(data.total)}</strong></div>
          {data.groups.map((g) => (
            <div key={g.cost_center_id || "sem"} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`ccc-group-${g.cost_center_id || "sem"}`}>
              <div className="flex items-center justify-between px-4 py-3 bg-brand-light">
                <span className="font-semibold text-brand">{g.name}</span>
                <span className="font-mono font-semibold text-brand">{brl(g.total)}</span>
              </div>
              <table className="w-full text-sm"><tbody>
                {g.categories.map((c) => (
                  <tr key={c.category_id || "sem"} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" data-testid={`ccc-cat-${g.cost_center_id || "sem"}-${c.category_id || "sem"}`}
                      onClick={() => setDrill({ title: `${g.name} — ${c.name}`, url: `/reports/transactions?start=${start}&end=${end}&kind=${kind}&cost_center_id=${g.cost_center_id || "__none__"}&category_id=${c.category_id || "__none__"}` })}>
                    <Td cls="text-slate-700 pl-8"><span className="inline-flex items-center gap-1"><ChevronRight className="h-3.5 w-3.5 text-slate-300" />{c.name}</span></Td>
                    <Td right mono bold>{brl(c.valor)}</Td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          ))}
        </div>
      )}
      {drill && <DrillModal {...drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

const RENDER = {
  "income-statement": IncomeStatement, "cashflow": CashFlow, "bank-balances": BankBalances,
  "expenses-by-cost-center": () => <ExpensesBy dim="cost_center" />, "expenses-by-category": () => <ExpensesBy dim="category" />,
  "cost-center-category": CostCenterCategory,
  "payables-aging": () => <Aging kind="payables" />, "receivables-aging": () => <Aging kind="receivables" />,
  "payments-by-supplier": PaymentsBySupplier, "movements": Movements, "works": Works,
  "monthly-comparison": MonthlyComparison, "audit": AuditReport,
};

export default function ReportsPage() {
  const { hasPermission, user } = useAuth();
  const [selected, setSelected] = useState("income-statement");
  const [exportUrl, setExportUrl] = useState(null);
  const printRef = useRef(null);
  const Current = RENDER[selected];
  const meta = REPORTS.find((r) => r.id === selected);
  const handlePrint = () => {
    const html = printRef.current?.innerHTML || "";
    printReport(html, meta.label, user?.name, `Relatório: ${meta.label}`);
  };
  const handleExcel = async () => {
    if (!exportUrl) return;
    try { await downloadFile(exportUrl, `relatorio_${selected}.xlsx`); }
    catch { toast.error("Falha ao exportar Excel."); }
  };
  return (
    <div className="space-y-6">
      <PageHeader title="Relatórios" subtitle="Análises financeiras gerenciais do Clube Monte Líbano — todos os valores calculados a partir dos lançamentos reais" />
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <aside className="bg-white rounded-xl border border-slate-200 shadow-sm p-2 h-fit" data-testid="reports-nav">
          {REPORTS.map((r) => (
            <button key={r.id} onClick={() => { setSelected(r.id); setExportUrl(null); }} data-testid={`report-tab-${r.id}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${selected === r.id ? "bg-brand text-white font-medium" : "text-slate-600 hover:bg-slate-50"}`}>
              <r.icon className="h-4 w-4 shrink-0" /> <span className="leading-tight">{r.label}</span>
            </button>
          ))}
        </aside>
        <section className="min-w-0">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2"><meta.icon className="h-5 w-5 text-brand" /><h2 className="font-heading font-semibold text-slate-900 text-lg">{meta.label}</h2></div>
            {hasPermission("reports.export") && (
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={handleExcel} data-testid="reports-export-excel" className="border border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" /> Exportar Excel
                </button>
                <button onClick={handlePrint} data-testid="reports-export-pdf" className="bg-brand hover:bg-brand-hover text-white font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                  <Printer className="h-4 w-4" /> Exportar PDF
                </button>
              </div>
            )}
          </div>
          <ExportCtx.Provider value={setExportUrl}>
            <div ref={printRef}><Current /></div>
          </ExportCtx.Provider>
        </section>
      </div>
    </div>
  );
}
